import { useCallback, useEffect, useState } from "react";
import { Button, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import { FaCheck, FaImage, FaTimes } from "react-icons/fa";
import { toast } from "react-toastify";
import axiosInstance from "../../axiosInstance";
import { API_URL_All } from "../../api";

const resolveUploadUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  return `${API_URL_All}${path}`;
};

const fmtVND = (v) =>
  Number(v || 0).toLocaleString("vi-VN", { style: "currency", currency: "VND" });

const unwrap = (res) => res?.data?.data ?? res?.data ?? {};

const ReconciliationPaymentConfirmModal = ({
  show,
  onHide,
  onSuccess,
  completeApi,
  payloadBase,
  summary = {},
  accountLabel = "Tài khoản nhận tiền",
}) => {
  const [bankAccounts, setBankAccounts] = useState([]);
  const [bankAccountId, setBankAccountId] = useState("");
  const [note, setNote] = useState("");
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [loadingAccounts, setLoadingAccounts] = useState(false);

  const resetForm = useCallback(() => {
    setBankAccountId("");
    setNote("");
    setFiles([]);
    setPreviews((prev) => {
      prev.forEach((p) => URL.revokeObjectURL(p.url));
      return [];
    });
  }, []);

  useEffect(() => {
    if (!show) {
      resetForm();
      return;
    }
    setLoadingAccounts(true);
    axiosInstance.get("/api/accounting/bank-accounts/active")
      .then((res) => {
        const body = unwrap(res);
        const accs = Array.isArray(body) ? body : body?.content || [];
        setBankAccounts(accs.filter((a) => a.active !== false));
      })
      .catch(() => toast.error("Không thể tải danh sách tài khoản"))
      .finally(() => setLoadingAccounts(false));
  }, [show, resetForm]);

  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files || []);
    const valid = selected.filter((f) => f.type.startsWith("image/"));
    if (valid.length < selected.length) {
      toast.warn("Chỉ chấp nhận file ảnh");
    }
    setFiles((prev) => [...prev, ...valid]);
    setPreviews((prev) => [
      ...prev,
      ...valid.map((f) => ({ name: f.name, url: URL.createObjectURL(f) })),
    ]);
    e.target.value = "";
  };

  const removePreview = (index) => {
    setPreviews((prev) => {
      const next = [...prev];
      URL.revokeObjectURL(next[index].url);
      next.splice(index, 1);
      return next;
    });
    setFiles((prev) => {
      const next = [...prev];
      next.splice(index, 1);
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!bankAccountId) {
      toast.warn("Vui lòng chọn tài khoản");
      return;
    }
    if (files.length === 0) {
      toast.warn("Vui lòng upload ít nhất một ảnh chứng từ");
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      files.forEach((f) => formData.append("files", f));
      const uploadRes = await axiosInstance.post(
        "/api/invoices/uploadReconciliationEvidence",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      if (!uploadRes.data?.success) {
        toast.error(uploadRes.data?.message || "Upload ảnh thất bại");
        return;
      }
      const evidencePaths = uploadRes.data?.data || [];

      const body = {
        ...(payloadBase || {}),
        id: payloadBase?.id ?? summary.id,
        bankAccountId: Number(bankAccountId),
        note,
        evidencePaths,
      };

      const response = await axiosInstance.post(completeApi, body);
      if (!response.data?.success) {
        toast.error(response.data?.message || "Xác nhận thất bại");
        return;
      }
      toast.success(response.data.message || "Xác nhận thanh toán thành công");
      onSuccess?.();
      onHide();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Xác nhận thất bại");
    } finally {
      setSubmitting(false);
    }
  };

  const formatAccountLabel = (acc) => {
    const bank = acc.bankName || "";
    const num = acc.accountNumber || "";
    return bank && num ? `${bank} - ${num}` : bank || num || `#${acc.id}`;
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Xác nhận thanh toán</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="border rounded p-3 mb-3 bg-light">
          <h6 className="fw-bold mb-3">Thông tin đối soát</h6>
          <Row className="g-2 small">
            <Col md={6}><span className="text-muted">Mã phiếu:</span> <strong>#{summary.id}</strong></Col>
            {summary.partyCode && (
              <Col md={6}><span className="text-muted">Mã KH/ĐT:</span> <strong>{summary.partyCode}</strong></Col>
            )}
            {summary.partyName && (
              <Col md={6}><span className="text-muted">Tên:</span> {summary.partyName}</Col>
            )}
            <Col md={6}>
              <span className="text-muted">Số tiền:</span>{" "}
              <strong className="text-primary">{fmtVND(summary.amount)}</strong>
            </Col>
            {summary.orderCount != null && (
              <Col md={6}><span className="text-muted">Số đơn:</span> {summary.orderCount}</Col>
            )}
            {summary.lineName && (
              <Col md={6}><span className="text-muted">Line:</span> {summary.lineName}</Col>
            )}
            {summary.dateText && (
              <Col md={6}><span className="text-muted">Ngày tạo:</span> {summary.dateText}</Col>
            )}
          </Row>
        </div>

        <Form.Group className="mb-3">
          <Form.Label>{accountLabel} <span className="text-danger">*</span></Form.Label>
          {loadingAccounts ? (
            <div className="text-muted small"><Spinner size="sm" animation="border" /> Đang tải...</div>
          ) : (
            <Form.Select value={bankAccountId} onChange={(e) => setBankAccountId(e.target.value)}>
              <option value="">-- Chọn tài khoản --</option>
              {bankAccounts.map((acc) => (
                <option key={acc.id} value={acc.id}>{formatAccountLabel(acc)}</option>
              ))}
            </Form.Select>
          )}
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>
            <FaImage className="me-1" /> Ảnh chứng từ chuyển/nhận tiền <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control type="file" accept="image/*" multiple onChange={handleFileChange} />
          <Form.Text className="text-muted">Có thể chọn nhiều ảnh (screenshot giao dịch)</Form.Text>
          {previews.length > 0 && (
            <div className="d-flex flex-wrap gap-2 mt-2">
              {previews.map((p, idx) => (
                <div key={idx} className="position-relative border rounded p-1" style={{ width: 100 }}>
                  <img src={p.url} alt={p.name} className="w-100 rounded" style={{ height: 70, objectFit: "cover" }} />
                  <button
                    type="button"
                    className="btn btn-sm btn-danger position-absolute top-0 end-0 p-0 px-1"
                    style={{ lineHeight: 1.2, fontSize: 10 }}
                    onClick={() => removePreview(idx)}
                    title="Xóa"
                  >
                    <FaTimes size={10} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </Form.Group>

        <Form.Group>
          <Form.Label>Ghi chú</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Nội dung ghi chú (tuỳ chọn)"
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={submitting}>
          Huỷ
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
          {submitting ? (
            <><Spinner size="sm" animation="border" className="me-1" /> Đang xử lý...</>
          ) : (
            <><FaCheck className="me-1" /> OK</>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ReconciliationPaymentConfirmModal;

export const buildPaymentSummary = (item, type = "customer") => {
  const isPartner = type === "partner";
  return {
    id: item.id,
    partyCode: isPartner ? (item.contactCode || item.code) : (item.customerCode || item.code),
    partyName: isPartner ? (item.contactName || item.name) : (item.customerName || item.name),
    amount: isPartner ? (item.contactFee ?? item.fee) : (item.fee ?? item.money),
    orderCount: item.orderCount,
    lineName: item.lineName,
    dateText: item.createdTimeText || item.insertTimeText || "",
  };
};
